import { bbox, points } from "@turf/turf";
import MansionIcon from "assets/icons/ic_map_type_apartment.svg";
import LandIcon from "assets/icons/ic_map_type_ground.svg";
import HouseIcon from "assets/icons/ic_map_type_used.svg";
import { EstateTypeNameEnum } from "common/enums/EstateTypeEnum";
import {
  clearMarker,
  latLngToCoordinatesPrimitive
} from "common/functions/mapUtilities";
import { useCallback, useContext, useEffect, useRef } from "react";
import { isMobile } from "react-device-detect";
import { SearchMapContext } from "../../context";
import { PricingPluginProps, ResultInfos } from "./interfaces";

export const useHooks = (props: PricingPluginProps) => {
  const { pricingMode } = props;

  const { map } = useContext(SearchMapContext);

  const zIndex = 10000;

  const pricingMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>(
    []
  );
  const pricingMarkerDraggingRef = useRef<boolean>(false);
  const pricingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const pricingPolygonRef = useRef<google.maps.Polygon | null>(null);
  const pricingDraggingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const pricingDraggingPolygonRef = useRef<google.maps.Polygon | null>(null);
  const pricingInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  // ポリゴン、ポリラインパス取得
  const getPath = useCallback(
    (pricingMarkers: google.maps.marker.AdvancedMarkerElement[]) => {
      const path = pricingMarkers.map((x) => x.position).filter((x) => x) as (
        | google.maps.LatLng
        | google.maps.LatLngLiteral
      )[];

      if (path.length > 2) {
        path.push(path[0]);
      }

      const hull = path.map((x) => [
        typeof x.lat === "number" ? x.lat : x.lat(),
        typeof x.lng === "number" ? x.lng : x.lng()
      ]) as number[][];

      return hull.map((x) => new google.maps.LatLng(x[0], x[1]));
    },
    []
  );

  // 囲みオブジェクト初期化
  const clearPricings = useCallback(() => {
    pricingDraggingPolylineRef.current?.setMap(null);
    pricingDraggingPolygonRef.current?.setMap(null);
    pricingPolylineRef.current?.setMap(null);
    pricingPolygonRef.current?.setMap(null);
    pricingInfoWindowRef.current?.close();

    pricingDraggingPolylineRef.current = null;
    pricingDraggingPolygonRef.current = null;
    pricingPolylineRef.current = null;
    pricingPolygonRef.current = null;
    pricingInfoWindowRef.current = null;
  }, []);

  // 囲みオブジェクト初期化
  const clearAllPricings = useCallback(() => {
    for (const pricingMarker of pricingMarkersRef.current) {
      clearMarker(pricingMarker);
    }

    pricingMarkersRef.current = [];

    clearPricings();
  }, [clearPricings]);

  const removeListener = useCallback(() => {
    if (clickListenerRef.current) {
      google.maps.event.removeListener(clickListenerRef.current);
    }
  }, []);

  // ピンポイント査定開始InfoWindow構築
  const constructInfoWindow = useCallback(
    (infos: ResultInfos, onPricingStartClick: (infos: ResultInfos) => void) => {
      const startLandButtonId = `pricing-info-window-start-land`;
      const startHouseButtonId = `pricing-info-window-start-house`;
      const startMansionButtonId = `pricing-info-window-start-mansion`;
      const pricingResults: string[] = [];

      const startButton =
        infos.less || infos.greater
          ? ""
          : `
        <div>
          <button
            id="${startLandButtonId}"
            class="pricing-start"
          >
            <img src="${LandIcon}" alt="土地アイコン" class="pricing-start-icon" />
            <div class="pricing-start-label">土地</div>
          </button>
          <button
            id="${startHouseButtonId}"
            class="pricing-start"
          >
            <img src="${HouseIcon}" alt="中古戸建アイコン" class="pricing-start-icon" />
            <div class="pricing-start-label">戸建</div>
          </button>
          <button
            id="${startMansionButtonId}"
            class="pricing-start"
          >
            <img src="${MansionIcon}" alt="マンションアイコン" class="pricing-start-icon" />
            <div class="pricing-start-label">マンション</div>
          </button>
        </div>`;

      if (infos.area) {
        pricingResults.push("ピンポイント査定");
      }

      const pricingResultContent = pricingResults
        .map((x) => `<div>${x}</div>`)
        .join("");

      const lessContent = infos.less
        ? `<div class="pricing_info_window_warning">※範囲が小さすぎます</div>`
        : "";

      const greaterContent = infos.greater
        ? `<div class="pricing_info_window_warning">※範囲が大きすぎます</div>`
        : "";

      const content = `<div class="pricing_info_window">${pricingResultContent}${lessContent}${greaterContent}${startButton}</div>`;

      const pricingInfoWindow = new google.maps.InfoWindow({
        content,
        position: infos.position,
        pixelOffset: new google.maps.Size(10, -10),
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + zIndex
      });

      pricingInfoWindow.addListener("closeclick", () => {
        clearAllPricings();
        props.onPricingEndClick();

        return false;
      });

      if (!infos.less && !infos.greater) {
        pricingInfoWindow.addListener("domready", () => {
          const startLandButtonDOM = document.getElementById(startLandButtonId);
          const startHouseButtonDOM =
            document.getElementById(startHouseButtonId);
          const startMansionButtonDOM =
            document.getElementById(startMansionButtonId);

          if (startLandButtonDOM) {
            startLandButtonDOM.addEventListener("click", () => {
              if (!infos.less && !infos.greater) {
                infos.estateType = EstateTypeNameEnum.土地;
                onPricingStartClick(infos);
              }
            });
          }

          if (startHouseButtonDOM) {
            startHouseButtonDOM.addEventListener("click", () => {
              if (!infos.less && !infos.greater) {
                infos.estateType = EstateTypeNameEnum.中古戸建;
                onPricingStartClick(infos);
              }
            });
          }

          if (startMansionButtonDOM) {
            startMansionButtonDOM.addEventListener("click", () => {
              if (!infos.less && !infos.greater) {
                infos.estateType = EstateTypeNameEnum.マンション;
                onPricingStartClick(infos);
              }
            });
          }
        });
      }

      return pricingInfoWindow;
    },
    [clearAllPricings, props]
  );

  // 囲みオブジェクト描画
  const drawPricingObject = useCallback(async () => {
    clearPricings();

    if (map) {
      const { spherical } = (await google.maps.importLibrary(
        "geometry"
      )) as unknown as google.maps.GeometryLibrary;

      if (pricingMarkersRef.current.length > 1) {
        const infos: ResultInfos = {
          position: null,
          area: 0,
          less: false,
          greater: false,
          points: [],
          tileOnZoom16s: [],
          estateType: null
        };

        const path = getPath(pricingMarkersRef.current);

        if (spherical) {
          pricingPolygonRef.current = new google.maps.Polygon({
            paths: path,
            fillColor: "#FFFFFF",
            fillOpacity: 0.5,
            strokeColor: "#0000FF",
            strokeWeight: 1.0,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + zIndex,
            clickable: false
          });

          infos.area =
            Math.floor(
              google.maps.geometry.spherical.computeArea(
                pricingPolygonRef.current.getPath()
              ) * 100
            ) / 100;

          infos.less = infos.area < 10 * 10;
          infos.points = pricingPolygonRef.current.getPath().getArray();

          pricingPolygonRef.current.setMap(map);
        }

        const builtBbox = bbox(points(path.map((x) => [x.lat(), x.lng()])));

        infos.position = new google.maps.LatLng(builtBbox[2], builtBbox[1]);

        const northWest = new google.maps.LatLng(builtBbox[2], builtBbox[1]);
        const southEast = new google.maps.LatLng(builtBbox[0], builtBbox[3]);

        const { tileCoordinate: tileOnZoom16OfNorthWest } =
          latLngToCoordinatesPrimitive(northWest.lat(), northWest.lng(), 16);

        const { tileCoordinate: tileOnZoom16OfSouthEast } =
          latLngToCoordinatesPrimitive(southEast.lat(), southEast.lng(), 16);

        for (
          let x = tileOnZoom16OfNorthWest.x;
          x <= tileOnZoom16OfSouthEast.x;
          x++
        ) {
          for (
            let y = tileOnZoom16OfNorthWest.y;
            y <= tileOnZoom16OfSouthEast.y;
            y++
          ) {
            infos.tileOnZoom16s.push(`${x},${y}`);
          }
        }

        infos.greater = infos.tileOnZoom16s.length > 64;

        if (infos.area) {
          pricingInfoWindowRef.current = constructInfoWindow(
            infos,
            props.onPricingStartClick
          );
          pricingInfoWindowRef.current.open(map);
        }
      }
    }
  }, [
    clearPricings,
    constructInfoWindow,
    getPath,
    map,
    props.onPricingStartClick
  ]);

  // ドラッグ中の囲みオブジェクト描画
  const drawPricingDraggingObject = useCallback(() => {
    pricingDraggingPolylineRef.current?.setMap(null);
    pricingDraggingPolygonRef.current?.setMap(null);

    if (map && pricingMarkersRef.current.length > 1) {
      const path = getPath(pricingMarkersRef.current);

      if (pricingMode) {
        pricingDraggingPolygonRef.current = new google.maps.Polygon({
          paths: path,
          fillColor: "#FFFFFF",
          fillOpacity: 0.0,
          strokeColor: "#00FFFF",
          strokeWeight: 0.8
        });

        pricingDraggingPolygonRef.current.setMap(map);
      }
    }
  }, [getPath, map, pricingMode]);

  const constructPricingPointMarker = useCallback(
    (position: google.maps.LatLng) => {
      const content = document.createElement("div");

      content.innerHTML = `
        <div class="pricing-point-marker"></div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: position,
        gmpDraggable: true,
        content: content,
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + zIndex
      });

      const onClick = () => {
        const index = pricingMarkersRef.current.findIndex((x) => {
          const markerPosition = marker.position;

          return x.position && markerPosition
            ? x.position instanceof google.maps.LatLng &&
              markerPosition instanceof google.maps.LatLng
              ? x.position.equals(markerPosition)
              : x.position.lat === markerPosition.lat &&
                x.position.lng === markerPosition.lng
            : false;
        });

        // 点の削除
        clearMarker(marker);
        pricingMarkersRef.current.splice(index, 1);

        drawPricingObject();
      };

      if (isMobile) {
        marker.element?.addEventListener("touchend", () => {
          if (!pricingMarkerDraggingRef.current) {
            onClick();
          }

          return false;
        });
      } else {
        marker.addListener("gmp-click", () => {
          onClick();

          return false;
        });
      }

      marker.addListener("drag", () => {
        pricingMarkerDraggingRef.current = true;
        drawPricingDraggingObject();

        return false;
      });

      marker.addListener("dragend", () => {
        drawPricingObject();
        pricingMarkerDraggingRef.current = false;

        return false;
      });

      return marker;
    },
    [drawPricingDraggingObject, drawPricingObject]
  );

  const invokeMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (map && pricingMode && event.latLng) {
        const marker = constructPricingPointMarker(event.latLng);

        marker.map = map;
        pricingMarkersRef.current.push(marker);

        drawPricingObject();
      }
    },
    [constructPricingPointMarker, drawPricingObject, map, pricingMode]
  );

  useEffect(() => {
    return () => {
      removeListener();
      clearAllPricings();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (map) {
      removeListener();
      clickListenerRef.current = map.addListener(
        "click",
        (event: google.maps.MapMouseEvent) => {
          invokeMapClick(event);
          return false;
        }
      );
    }
  }, [invokeMapClick, map, removeListener]);

  useEffect(() => {
    if (map) {
      map.setOptions({
        draggableCursor: pricingMode ? "default" : "grab"
      });
    }
  }, [map, pricingMode]);

  useEffect(() => {
    clearAllPricings();
  }, [clearAllPricings, pricingMode]);

  return { map } as const;
};
